import { useState } from "react";
import { Loader2, Plus, Trash2, Phone } from "lucide-react";
import { empresaNotificacionesService } from "../../services/api";
import Swal from "sweetalert2";

// Roles — mismos keys que DESTINATARIOS en ReglaDrawer para que el resolver los encuentre
const ROLES = [
  { key: "conductor",                   label: "Conductor del viaje" },
  { key: "TRANSPORTISTA:admin",         label: "Transportista — Admin" },
  { key: "TRANSPORTISTA:transportista", label: "Transportista — Usuario transportista" },
  { key: "OPERADOR:admin",              label: "Operador Logístico — Admin" },
  { key: "OPERADOR:operador_logistico", label: "Operador Logístico — Usuario operador" },
  { key: "CLIENTE:admin",              label: "Cliente — Admin" },
  { key: "CLIENTE:cliente",            label: "Cliente — Usuario cliente" },
];

// Valida formato E.164: + seguido de 7 a 15 dígitos
function esFormatoValido(tel) {
  return /^\+[1-9]\d{6,14}$/.test(tel.trim());
}

function FilaRol({ rolKey, label, telefonos, onAgregar, onEliminar, guardando }) {
  const [input, setInput] = useState("");
  const [error, setError]  = useState("");

  const handleAgregar = () => {
    const tel = input.trim();
    if (!esFormatoValido(tel)) {
      setError("Formato inválido. Usá E.164: +595981123456");
      return;
    }
    if (telefonos.includes(tel)) {
      setError("Este número ya está en la lista.");
      return;
    }
    setError("");
    setInput("");
    onAgregar(rolKey, tel);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleAgregar();
  };

  return (
    <div className="border border-gray-200 rounded-xl p-4 bg-white">
      {/* Encabezado del rol */}
      <div className="flex items-center gap-2 mb-3">
        <Phone size={15} className="text-green-600 shrink-0" />
        <span className="text-sm font-semibold text-gray-800">{label}</span>
        {telefonos.length > 0 && (
          <span className="ml-auto text-xs text-gray-400">{telefonos.length} número{telefonos.length !== 1 ? "s" : ""}</span>
        )}
      </div>

      {/* Lista de teléfonos configurados */}
      {telefonos.length > 0 ? (
        <ul className="flex flex-col gap-1.5 mb-3">
          {telefonos.map((tel) => (
            <li key={tel} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-1.5">
              <span className="text-sm font-mono text-gray-700">{tel}</span>
              <button
                onClick={() => onEliminar(rolKey, tel)}
                disabled={guardando}
                className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-40"
                title="Eliminar"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-400 mb-3 italic">Sin números configurados.</p>
      )}

      {/* Input para agregar */}
      <div className="flex gap-2">
        <div className="flex-1">
          <input
            type="tel"
            value={input}
            onChange={(e) => { setInput(e.target.value); setError(""); }}
            onKeyDown={handleKeyDown}
            placeholder="+595981123456"
            className={`w-full text-sm border rounded-lg px-3 py-1.5 font-mono focus:outline-none focus:ring-2 focus:ring-green-400 ${
              error ? "border-red-400" : "border-gray-300"
            }`}
          />
          {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
        <button
          onClick={handleAgregar}
          disabled={guardando || !input}
          className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-40 shrink-0"
        >
          {guardando ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Agregar
        </button>
      </div>
    </div>
  );
}

export default function ContactosSmsConfig({ empresaId, contactosIniciales = {} }) {
  const [contactos, setContactos]   = useState(contactosIniciales);
  const [guardando, setGuardando]   = useState(false);

  const guardar = async (nuevosContactos) => {
    setGuardando(true);
    try {
      await empresaNotificacionesService.updateContactosSms(empresaId, nuevosContactos);
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error al guardar",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setGuardando(false);
    }
  };

  const handleAgregar = async (rol, telefono) => {
    const actualizados = {
      ...contactos,
      [rol]: [...(contactos[rol] ?? []), telefono],
    };
    setContactos(actualizados);
    await guardar(actualizados);
  };

  const handleEliminar = async (rol, telefono) => {
    const actualizados = {
      ...contactos,
      [rol]: (contactos[rol] ?? []).filter((t) => t !== telefono),
    };
    setContactos(actualizados);
    await guardar(actualizados);
  };

  return (
    <div className="mt-8">
      {/* Encabezado de sección */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
          <Phone size={16} className="text-green-700" />
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900">Contactos SMS</h3>
          <p className="text-xs text-gray-500">
            Números que reciben alertas por SMS según el rol. Formato internacional obligatorio: <span className="font-mono">+595981123456</span>
          </p>
        </div>
        {guardando && <Loader2 size={16} className="animate-spin text-green-500 ml-auto" />}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ROLES.map(({ key, label }) => (
          <FilaRol
            key={key}
            rolKey={key}
            label={label}
            telefonos={contactos[key] ?? []}
            onAgregar={handleAgregar}
            onEliminar={handleEliminar}
            guardando={guardando}
          />
        ))}
      </div>
    </div>
  );
}
